import {
  Container,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { EmptyStateIcon, Filter } from "assets/Icon";
import {
  TRADE_HISTORY_TABLE_HEADERS,
  TRADE_HISTORY_TABLE_HEADERS_ORDER,
  TRADE_HISTORY_TABS,
} from "./constants";
import {
  cancelOrder,
  getHistoryOrderForm,
  getHistoryOrderList,
  getTradeForm,
  getTradeList,
} from "store/slices/trade/tradeSlice";
import { useEffect, useState } from "react";

import EmptyState from "components/EmptyState";
import HistoryBox from "pages/BasicTrade/components/TradeHistory/Components/HistoryBox";
import NumberFormats from "components/NumberFormats";
import OrderCancelButton from "./Components/OrderCancelButton";
import Pagination from "components/Pagination";
import { TRADE_SIDE } from "pages/BasicTrade/components/constants";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import TradeHistoryFilters from "./Components/TradeHistoryFilters";
import TradeHistoryTabs from "./Components/TradeHistoryTabs";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatDateToJalali } from "utils/helpers/time";

function TradeHistory({ hideTab = false, defaultTradeTab }) {
  const isMobileView = useIsMobileView();
  const dispatch = useDispatch();
  const theme = useTheme();
  const tradeHistory = useSelector((state) => state.trade.tradeHistory);

  const [searchParams] = useSearchParams();
  const tradeTab = +searchParams.get("tradeTab") || defaultTradeTab || 0;
  const typeId = +searchParams.get("typeId") || 0;
  const sideId = +searchParams.get("sideId") || 0;
  const coinSourceId = +searchParams.get("coinSourceId") || -1;
  const coinDestinationId = +searchParams.get("coinDestinationId") || -1;
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const page = +searchParams.get("page") || 1;
  const perPage = +searchParams.get("perPage") || 10;
  const brokerId = +searchParams.get("brokerId") || 0;
  const basketId = +searchParams.get("basketId") || -1;

  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const getTradeHistory = async (body) => {
    setLoading(true);
    if (tradeTab === TRADE_HISTORY_TABS.orderHistory.value) {
      await dispatch(getHistoryOrderList(body));
    } else if (tradeTab === TRADE_HISTORY_TABS.tradeHistory.value) {
      await dispatch(getTradeList(body));
    }
    setLoading(false);
  };

  const getTradeHistoryForm = (body) => {
    if (tradeTab === TRADE_HISTORY_TABS.orderHistory.value) {
      dispatch(getHistoryOrderForm());
    } else if (tradeTab === TRADE_HISTORY_TABS.tradeHistory.value) {
      dispatch(getTradeForm());
    }
  };

  const handleChangePaginate = ({ page, perPage }) => {
    getTradeHistory({
      coinSourceId,
      typeId,
      sideId,
      brokerId,
      basketId,
      coinDestinationId,
      startDate: formatDateToJalali(startDate),
      pageSize: perPage,
      pageNumber: page,
      endDate: formatDateToJalali(endDate),
    });
  };

  useEffect(() => {
    getTradeHistory({
      coinSourceId,
      typeId,
      brokerId,
      basketId,
      sideId,
      coinDestinationId,
      startDate: formatDateToJalali(startDate),
      pageSize: perPage,
      pageNumber: page,
      endDate: formatDateToJalali(endDate),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tradeTab,
    typeId,
    sideId,
    brokerId,
    basketId,
    coinSourceId,
    coinDestinationId,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    getTradeHistoryForm();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeTab]);

  const sideRowColor = (item) => {
    if (item?.stateId === 3) return `${theme.palette.background.lightest}20`;
    else if (item?.sideId === TRADE_SIDE.Buy.id)
      return `${theme.palette.success.main}20`;
    else return `${theme.palette.error.main}20`;
  };

  const handleCancelOrder = async (order) => {
    await dispatch(cancelOrder({ orderId: order?.id }), 'Order have been canceled');
    await getTradeHistory({
      coinSourceId,
      typeId,
      sideId,
      coinDestinationId,
      startDate: formatDateToJalali(startDate),
      pageSize: perPage,
      pageNumber: page,
      endDate: formatDateToJalali(endDate),
    });
  };

  const formattedItems = tradeHistory?.data?.map((item) => ({
    ...item,
    color: sideRowColor(item),
    date: `${item?.date} ${item?.time}`,
    market: (
      <>
        <Typography variant="h6" component="span" color="text.main">
          {item?.coinSourceName}
        </Typography>
        /
        <Typography
          variant="subtitle1"
          component="small"
          color="text.secondary"
        >
          {item?.coinDestinationName}
        </Typography>
      </>
    ),
    side: (
      <Typography
        color={
          item?.sideId === TRADE_SIDE.Buy.id
            ? theme.palette.success.main
            : theme.palette.error.main
        }
      >
        {item?.side}
      </Typography>
    ),
    filled: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.filled} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinSourceName}
        </Typography>
      </Typography>
    ),
    fee: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.fee} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinFeeName}
        </Typography>
      </Typography>
    ),
    price: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.price} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinDestinationName}
        </Typography>
      </Typography>
    ),
    total: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.total} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinDestinationName}
        </Typography>
      </Typography>
    ),
    action: <OrderCancelButton onClick={handleCancelOrder} item={item} />,
  }));

  return (
    <Container maxWidth="100%">
      {!hideTab && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TradeHistoryTabs />
          </Grid>

          <Grid item xs={12} textAlign="right">
            <IconButton
              onClick={() => {
                setShowFilter((v) => !v);
              }}
              aria-label="filter"
            >
              <Filter color={theme.palette.text.main} />
            </IconButton>
          </Grid>
        </Grid>
      )}

      {showFilter && <TradeHistoryFilters />}

      <Grid container mt={2} spacing={2}>
        <>
          {isMobileView ? (
            <>
              {tradeHistory?.data?.length ? (
                tradeHistory?.data?.map((item, index) => (
                  <Grid key={index} item xs={12}>
                    <HistoryBox
                      item={item}
                      hasCancel={
                        tradeTab === TRADE_HISTORY_TABS.orderHistory.value
                      }
                      onCancel={handleCancelOrder}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <EmptyState
                    text="No History"
                    icon={
                      <EmptyStateIcon color={theme.palette.text.secondary} />
                    }
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              {!loading ? (
                <Grid item xs={12}>
                  <Table
                    headers={
                      tradeTab === TRADE_HISTORY_TABS.orderHistory.value
                        ? TRADE_HISTORY_TABLE_HEADERS_ORDER
                        : TRADE_HISTORY_TABLE_HEADERS
                    }
                    items={formattedItems}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TableSkeleton />
                </Grid>
              )}
            </>
          )}
        </>

        <Grid item xs={12}>
          <Pagination
            perPage={tradeHistory?.pageSize}
            count={tradeHistory?.totalPages}
            onChange={handleChangePaginate}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default TradeHistory;
